import { useEffect, useRef, useState } from 'react';
import { useAcombaBridgeStore } from '../stores/acombaBridgeStore';

const RETRY_WINDOW_MS = 10_000;
const POLL_INTERVAL_MS = 60_000;

export type AcombaBridgeGateStatus = 'checking' | 'blocked' | 'ready';

export interface UseAcombaBridgeGateResult {
    status: AcombaBridgeGateStatus;
    /** Only meaningful while status is 'blocked' — a no-op otherwise. */
    retry: () => void;
    retrying: boolean;
}

/**
 * Gates the whole app shell on the bridge session for an Acomba desktop launch — nothing downstream
 * (HomeGreeting's userName, company data, sync) is trustworthy before `sessionStatus` reaches
 * 'connected', so 'reachable' (the bridge process merely answering /health) is not good enough here.
 *
 * On going into 'checking': one immediate attempt, then one retry within RETRY_WINDOW_MS if the first
 * fails (skipped for a manual retry — see below). Two failures in a row stop all automatic checking
 * and move to 'blocked'. A success at any point starts a background poll every POLL_INTERVAL_MS; if
 * that poll later finds the session gone, the exact same checking→retry→blocked sequence runs again —
 * one state machine for both the startup and a later mid-session disconnect.
 *
 * `retry()` (only meaningful from 'blocked') is a single immediate attempt — no second 10s-window
 * attempt — matching "one click, one check" rather than replaying the startup sequence.
 *
 * `isAcomba === false` (AcceoConnect/AcombaGo) never enters 'checking'/'blocked' — no bridge exists
 * for those sessions, so no timer is ever created and status stays 'ready'.
 */
export function useAcombaBridgeGate(isAcomba: boolean): UseAcombaBridgeGateResult {
    const [status, setStatus] = useState<AcombaBridgeGateStatus>(isAcomba ? 'checking' : 'ready');
    const [cycle, setCycle] = useState(0);
    const retrying = useAcombaBridgeStore((s) => s.isCheckingHealth);

    const cancelledRef = useRef(false);
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // true only for the single effect run triggered by retry() — collapses that run's sequence to one
    // attempt instead of the normal attempt+10s-retry pair. Reset before every poll-triggered restart.
    const manualRetryRef = useRef(false);

    useEffect(() => {
        cancelledRef.current = false;

        function stopTimers() {
            if (pollIntervalRef.current !== null) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
            if (retryTimeoutRef.current !== null) { clearTimeout(retryTimeoutRef.current); retryTimeoutRef.current = null; }
        }

        function enterReady() {
            stopTimers();
            setStatus('ready');
            pollIntervalRef.current = setInterval(handlePollTick, POLL_INTERVAL_MS);
        }

        function enterBlocked() {
            stopTimers();
            setStatus('blocked');
        }

        function handlePollTick() {
            void (async () => {
                await useAcombaBridgeStore.getState().checkBridgeHealth();
                if (cancelledRef.current) return;
                if (useAcombaBridgeStore.getState().sessionStatus !== 'connected') {
                    stopTimers();
                    manualRetryRef.current = false;
                    setStatus('checking');
                    setCycle((c) => c + 1);
                }
            })();
        }

        async function runCheckSequence() {
            const isManual = manualRetryRef.current;

            await useAcombaBridgeStore.getState().checkBridgeHealth();
            if (cancelledRef.current) return;
            if (useAcombaBridgeStore.getState().sessionStatus === 'connected') {
                enterReady();
                return;
            }
            if (isManual) {
                enterBlocked();
                return;
            }

            retryTimeoutRef.current = setTimeout(() => {
                void (async () => {
                    await useAcombaBridgeStore.getState().checkBridgeHealth();
                    if (cancelledRef.current) return;
                    if (useAcombaBridgeStore.getState().sessionStatus === 'connected') enterReady();
                    else enterBlocked();
                })();
            }, RETRY_WINDOW_MS);
        }

        if (!isAcomba) {
            setStatus('ready');
            return () => { cancelledRef.current = true; };
        }

        setStatus('checking');
        void runCheckSequence();

        return () => {
            cancelledRef.current = true;
            stopTimers();
        };
        // cycle is a deliberate restart trigger (poll-detected disconnect, or a manual retry); isAcomba
        // flipping also restarts the whole machine from a clean slate.
    }, [isAcomba, cycle]);

    function retry() {
        if (status !== 'blocked') return;
        manualRetryRef.current = true;
        setStatus('checking');
        setCycle((c) => c + 1);
    }

    return { status, retry, retrying };
}
